import { TextField, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

interface InlineEditCellProps {
  value: string;
  onSave: (newValue: string) => Promise<void>;
  placeholder?: string;
  disabled?: boolean;
  fontFamily?: string;
}

const InlineEditCell: React.FC<InlineEditCellProps> = ({
  value,
  onSave,
  placeholder = "—",
  disabled = false,
  fontFamily,
}) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleSave = async () => {
    const trimmed = editValue.trim();
    if (trimmed === (value || "").trim()) {
      setEditing(false);
      return;
    }
    try {
      setSaving(true);
      await onSave(trimmed);
      setEditing(false);
    } catch {
      // Revert on error — parent handles showing the error message
      setEditValue(value || "");
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      setEditValue(value || "");
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <TextField
        inputRef={inputRef}
        size="small"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        disabled={saving}
        variant="standard"
        sx={{
          minWidth: 80,
          "& .MuiInput-input": {
            fontSize: "0.875rem",
            fontFamily: fontFamily || "monospace",
            textAlign: "center",
            py: 0,
          },
        }}
      />
    );
  }

  return (
    <Typography
      variant="body2"
      fontFamily={fontFamily || "monospace"}
      onClick={(e) => {
        if (!disabled) {
          e.stopPropagation();
          setEditValue(value || "");
          setEditing(true);
        }
      }}
      sx={{
        cursor: disabled ? "default" : "pointer",
        color: value ? "text.primary" : "text.disabled",
        "&:hover": disabled
          ? {}
          : {
              backgroundColor: "action.hover",
              borderRadius: 0.5,
            },
        px: 0.5,
        py: 0.25,
        minHeight: 24,
        display: "inline-block",
      }}
    >
      {value || placeholder}
    </Typography>
  );
};

export default InlineEditCell;
